import React, { useState, useRef } from 'react';

interface DocumentUploadInputProps {
  label: string;
  onFileSelect: (file: File | null) => void;
  acceptedTypes?: string;
  maxSize?: number; // in MB
  error?: string;
  className?: string;
  documentStatus?: 'pending_approval' | 'approved' | 'rejected';
  onReupload?: () => void;
}

const DocumentUploadInput: React.FC<DocumentUploadInputProps> = ({
  label,
  onFileSelect,
  acceptedTypes = '.pdf,.jpg,.jpeg,.png',
  maxSize = 5,
  error,
  className = '',
  documentStatus,
  onReupload,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        setUploadError(`File size must be less than ${maxSize}MB`);
        setSelectedFile(null);
        onFileSelect(null);
        return;
      }

      // Check file type
      const allowedTypes = acceptedTypes.split(',').map(type => type.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setUploadError(`File type must be ${acceptedTypes}`);
        setSelectedFile(null);
        onFileSelect(null);
        return;
      }

      setUploadError('');
      setSelectedFile(file);
      onFileSelect(file);
    } else {
      setSelectedFile(null);
      onFileSelect(null);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'pending_approval': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending_approval': return 'Pending Approval';
      default: return 'Not Uploaded';
    }
  };

  return (
    <div className={`document-upload ${className}`}>
      <div className="document-header">
        <label className="upload-label">
          {label}
          {documentStatus && (
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(documentStatus) }}
            >
              {getStatusText(documentStatus)}
            </span>
          )}
        </label>
        {documentStatus && onReupload && (
          <button
            type="button"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            className="reupload-btn"
          >
            {documentStatus === 'approved' ? 'Replace' : 'Reupload'}
          </button>
        )}
      </div>

      <label className="upload-label">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          accept={acceptedTypes}
          className="file-input"
        />
        <div className="upload-area">
          {selectedFile ? (
            <div className="file-info">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
          ) : (
            <div className="upload-placeholder">
              <span>Click to select file</span>
              <span className="upload-hint">Max {maxSize}MB, {acceptedTypes}</span>
            </div>
          )}
        </div>
      </label>
      {(uploadError || error) && (
        <span className="error-message">{uploadError || error}</span>
      )}
    </div>
  );
};

export default DocumentUploadInput;
